import { useState, useRef } from 'react';
import api from './api';
import { useFilePreview, FilePreviewModal } from './FilePreview';

// Спільний компонент для кабінету фрілансера (MyProjectsPage.js — детальна
// панель проєкту, MyDocumentsPage.js — розділ PROJECTS) — інтерактивне
// дерево документів проєкту з можливістю створювати/перейменовувати/
// видаляти папки й файли, БЕЗ доступу до форми редагування проєкту.
//
// Права (рішення 2026-08):
//  - area="project" (Project Documents, спільна папка — бачать усі
//    призначені фрілансери однаково): створити підпапку/завантажити файл
//    можна в корені або у ВЛАСНІЙ раніше створеній підпапці; перейменувати/
//    видалити — лише своє (createdByFreelancerId/uploadedByFreelancerId).
//  - area="crew" (Crew/<Ім'я>, приватна папка фрілансера): повний CRUD у
//    власному піддереві, крім самого кореня (isRoot) — той не
//    перейменовується й не видаляється.
// Розгорнуто/згорнуто — локальний UI-стан, на бекенді не зберігається.

function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp'].includes(ext)) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx'].includes(ext)) return '📊';
  if (['zip','rar','7z'].includes(ext)) return '🗜️';
  if (['mp4','mov','avi'].includes(ext)) return '🎬';
  return '📎';
}

// Файл-рядок, що сам обирає URL завантаження залежно від типу:
// project_file → /projects/:id/files/:fileId/download,
// personal_document → /freelancers/:freelancerId/documents/:docId/download
// (два різні джерела з незалежними числовими id — без type їх не розрізнити).
export function FileEntry({ file, projectId, freelancerId }) {
  const { previewFile, openPreview, closePreview } = useFilePreview();
  const urls = file.type === 'personal_document'
    ? { url: api.freelancers.documentUrl(freelancerId, file.id), downloadUrl: api.freelancers.documentUrl(freelancerId, file.id, { download: true }) }
    : { url: api.projects.fileUrl(projectId, file.id), downloadUrl: api.projects.fileUrl(projectId, file.id, { download: true }) };
  return (
    <>
      <div
        onClick={() => openPreview({ url: urls.url, downloadUrl: urls.downloadUrl, name: file.name })}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--info-text)', fontSize: '13px', cursor: 'pointer', padding: '5px 0 5px 20px' }}>
        <span>{fileIcon(file.name)}</span>{file.name}
        {file.type === 'personal_document' && (
          <span style={{ color: 'var(--text-disabled)', fontSize: '11px' }}>(from your profile)</span>
        )}
      </div>
      <FilePreviewModal file={previewFile} onClose={closePreview} />
    </>
  );
}

export function FolderNode({ folder, projectId, freelancerId, area, isRoot, myId, onRefresh, depth = 0 }) {
  const [isOpen, setIsOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [addingFolder, setAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(folder.name);
  const fileInputRef = useRef(null);

  const linked = folder.linkedDocuments || [];
  const files = folder.files || [];
  const subfolders = folder.subfolders || [];
  const isOwn = area === 'crew' ? true : folder.createdByFreelancerId === myId;
  const canManageFolder = !isRoot && isOwn;   // перейменувати / видалити цю папку
  const canAddHere      = isRoot || isOwn;    // створити підпапку / вантажити файл сюди

  const run = fn => {
    setBusy(true); setError('');
    return fn().then(onRefresh).catch(e => setError(e.message || 'Failed')).finally(() => setBusy(false));
  };

  const createFolder = () => {
    const name = newFolderName.trim();
    if (!name) return;
    run(() => area === 'crew'
      ? api.projects.createMyCrewFolder(projectId, name, folder.id)
      : api.projects.createProjectDocFolder(projectId, name, folder.id)
    ).then(() => { setNewFolderName(''); setAddingFolder(false); });
  };

  const renameFolder = () => {
    const name = renameValue.trim();
    if (!name || name === folder.name) { setRenaming(false); return; }
    run(() => area === 'crew'
      ? api.projects.renameMyCrewFolder(projectId, folder.id, name)
      : api.projects.renameProjectDocFolder(projectId, folder.id, name)
    ).then(() => setRenaming(false));
  };

  const deleteFolder = () => {
    if (!window.confirm(`Delete folder "${folder.name}" and everything inside it?`)) return;
    run(() => area === 'crew'
      ? api.projects.deleteMyCrewFolder(projectId, folder.id)
      : api.projects.deleteProjectDocFolder(projectId, folder.id));
  };

  const uploadFiles = fileList => {
    run(() => area === 'crew'
      ? api.projects.uploadMyCrewFolderFiles(projectId, fileList, folder.id)
      : api.projects.uploadProjectDocFiles(projectId, folder.id, fileList));
  };

  // file.id тут = реальний numeric project_files.id (GET /mine віддає вже
  // "очищене" дерево, де id файлу і є serverId — окремого поля serverId нема,
  // на відміну від сирого docFolders, яке бачить адмін).
  const deleteFile = file => {
    if (file.type === 'personal_document') return; // профільні — лише через Profile
    if (!window.confirm(`Delete "${file.name}"?`)) return;
    run(() => area === 'crew'
      ? api.projects.deleteMyCrewFile(projectId, file.id)
      : api.projects.deleteProjectDocFile(projectId, file.id));
  };

  const allFiles = [
    ...linked.map(d => ({ ...d, deletable: false })),
    ...files.map(f => ({ ...f, deletable: area === 'crew' || f.uploadedByFreelancerId === myId })),
  ];
  const hasContent = allFiles.length > 0 || subfolders.length > 0;

  return (
    <div style={{ marginLeft: depth > 0 ? '16px' : 0, marginTop: depth > 0 ? '6px' : 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
        <span onClick={() => setIsOpen(o => !o)} style={{ fontSize: '10px', color: 'var(--text-dim)', cursor: 'pointer', width: '10px' }}>
          {isOpen ? '▼' : '▶'}
        </span>
        {renaming ? (
          <>
            <input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && renameFolder()}
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '5px', color: 'var(--text-primary)', padding: '3px 8px', fontSize: '12px' }} />
            <button disabled={busy} onClick={renameFolder} style={{ background: 'var(--accent)', color: '#fff', border: 'none', padding: '3px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}>Save</button>
            <button onClick={() => { setRenaming(false); setRenameValue(folder.name); }} style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '3px 8px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}>Cancel</button>
          </>
        ) : (
          <span onClick={() => setIsOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-body)', fontSize: '13px', fontWeight: depth === 0 ? 700 : 500, cursor: 'pointer' }}>
            {area === 'crew' && isRoot ? '👤' : '📁'} {folder.name}
          </span>
        )}
        <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
          {canAddHere && (
            <button disabled={busy} onClick={() => setAddingFolder(o => !o)} title="New subfolder"
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>+ 📁</button>
          )}
          {canAddHere && (
            <button disabled={busy} onClick={() => fileInputRef.current?.click()} title="Upload file"
              style={{ background: 'var(--accent)', color: '#fff', border: 'none', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
              {busy ? '…' : '+ File'}
            </button>
          )}
          {canManageFolder && !renaming && (
            <>
              <button disabled={busy} onClick={() => setRenaming(true)} title="Rename"
                style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>✎</button>
              <button disabled={busy} onClick={deleteFolder} title="Delete folder"
                style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>🗑</button>
            </>
          )}
        </div>
        {/* Array.from — робить реальну копію FileList. e.target.files є
            live-об'єктом: якщо скинути e.target.value одразу після читання
            files, той самий об'єкт спорожняється і посилання fl теж стає
            порожнім, тому наступний вибір файлу нічого не вивантажує. */}
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
          onChange={e => { const fl = Array.from(e.target.files || []); e.target.value = ''; if (fl.length) uploadFiles(fl); }} />
      </div>

      {error && <p style={{ color: 'var(--danger-text-soft)', fontSize: '11px', margin: '0 0 4px 20px' }}>{error}</p>}

      {isOpen && (
        <>
          {addingFolder && (
            <div style={{ display: 'flex', gap: '6px', margin: '0 0 8px 20px' }}>
              <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createFolder()}
                placeholder="Folder name..."
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '5px', color: 'var(--text-primary)', padding: '4px 8px', fontSize: '12px' }} />
              <button disabled={busy} onClick={createFolder} style={{ background: 'var(--accent)', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}>Add</button>
              <button onClick={() => { setAddingFolder(false); setNewFolderName(''); }} style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-dim)', padding: '4px 10px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}>Cancel</button>
            </div>
          )}

          {!hasContent && <p style={{ color: 'var(--text-disabled)', fontSize: '11px', margin: '0 0 4px 20px' }}>Empty</p>}

          {allFiles.map(file => (
            <div key={`${file.type || 'file'}-${file.id}`} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ flex: 1 }}><FileEntry file={file} projectId={projectId} freelancerId={freelancerId} /></div>
              {file.deletable && (
                <button disabled={busy} onClick={() => deleteFile(file)} title="Delete file"
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-disabled)', cursor: 'pointer', fontSize: '13px', marginRight: '4px' }}>✕</button>
              )}
            </div>
          ))}

          {subfolders.map(sub => (
            <FolderNode key={sub.id} folder={sub} projectId={projectId} freelancerId={freelancerId}
              area={area} isRoot={false} myId={myId} onRefresh={onRefresh} depth={depth + 1} />
          ))}
        </>
      )}
    </div>
  );
}
